import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useData } from '@hooks/useData';
import { useTranslation } from 'react-i18next';
import {
    buildCookieConsentConfig,
    buildPolicyUrl,
    COOKIE_CONSENT_STORAGE_NAME,
    COOKIE_CONSENT_ELEMENT_ID,
    KlaroConfig,
} from '@services/cookieConsent';

declare global {
    interface Window {
        klaro?: {
            setup: (config: KlaroConfig) => void;
            show: (config?: KlaroConfig) => void;
            getManager: (config?: KlaroConfig) => {
                confirmed: boolean;
                watch: (watcher: KlaroWatcher) => void;
                unwatch: (watcher: KlaroWatcher) => void;
            };
        };
    }
}

type KlaroWatcher = {
    update: (manager: { confirmed: boolean }, eventName: string) => void;
};

const DEFAULT_LANGUAGES = ['pt', 'en', 'fr', 'es', 'de'];

const hasStoredDecision = () => {
    try {
        return Boolean(localStorage.getItem(COOKIE_CONSENT_STORAGE_NAME));
    } catch {
        return false;
    }
};

export const useCookieConsent = () => {
    const { allLanguages, allSettings } = useData();
    const { t, i18n } = useTranslation();
    const [hasDecision, setHasDecision] = useState<boolean>(() => hasStoredDecision());
    const [isReady, setIsReady] = useState<boolean>(false);
    const configRef = useRef<KlaroConfig | null>(null);
    const watcherRef = useRef<KlaroWatcher | null>(null);
    const initialPromptShownRef = useRef<boolean>(false);

    const fallbackLanguage = useMemo(() => {
        const apiDefault = allLanguages?.data?.find((language) => language.default === 1)?.language_code;
        return apiDefault || 'pt';
    }, [allLanguages]);

    const hasAnalyticsConfig = useMemo(() => {
        const analyticsSetting = (allSettings?.data as Record<string, unknown> | undefined)?.['google_analytics'];
        if (typeof analyticsSetting === 'string') {
            return analyticsSetting.trim().length > 0;
        }
        return Boolean(analyticsSetting);
    }, [allSettings]);

    const languages = useMemo(() => {
        const apiLanguages =
            allLanguages?.data?.map((language) => language.language_code).filter((code): code is string => Boolean(code)) || [];
        return Array.from(new Set([...DEFAULT_LANGUAGES, ...apiLanguages]));
    }, [allLanguages]);

    const resolvePolicyUrl = useCallback((language?: string) => buildPolicyUrl(language, fallbackLanguage), [fallbackLanguage]);

    const cookieConfig = useMemo(() => {
        if (!hasAnalyticsConfig) {
            return null;
        }
        return buildCookieConsentConfig({
            languages,
            fallbackLanguage,
            currentLanguage: i18n.language,
            getTranslator: i18n.getFixedT.bind(i18n),
            onPreferenceChange: (service, consent) => {
                window.dispatchEvent(new CustomEvent('cookie-preferences-change', { detail: { service, consent } }));
            },
            policyUrlResolver: resolvePolicyUrl,
        });
    }, [languages, fallbackLanguage, i18n, resolvePolicyUrl, hasAnalyticsConfig]);

    const detachWatcher = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !watcherRef.current || !configRef.current) {
            return;
        }
        const manager = window.klaro.getManager(configRef.current);
        manager?.unwatch(watcherRef.current);
        watcherRef.current = null;
    }, [hasAnalyticsConfig]);

    const attachWatcher = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !configRef.current) {
            return;
        }
        const manager = window.klaro.getManager(configRef.current);
        if (!manager) {
            return;
        }
        detachWatcher();
        const watcher: KlaroWatcher = {
            update: (_, eventName) => {
                if (eventName === 'saveConsents') {
                    setHasDecision(true);
                }
            },
        };
        watcherRef.current = watcher;
        manager.watch(watcher);
        setHasDecision(manager.confirmed || hasStoredDecision());
    }, [detachWatcher, hasAnalyticsConfig]);

    useEffect(() => {
        configRef.current = cookieConfig;
    }, [cookieConfig]);

    useEffect(() => {
        let cancelled = false;

        const loadKlaro = async () => {
            if (typeof window === 'undefined' || !cookieConfig || !hasAnalyticsConfig) return;

            if (!window.klaro) {
                const klaroModule = await import('klaro/dist/klaro-no-translations.js');
                const klaroInstance = (klaroModule && (klaroModule.default || klaroModule)) as Window['klaro'];
                if (klaroInstance) {
                    window.klaro = klaroInstance;
                }
            }
            if (cancelled || !window.klaro || !configRef.current) {
                return;
            }

            const alreadyDecided = hasStoredDecision();
            window.klaro.setup(configRef.current);
            attachWatcher();
            setIsReady(true);
            setHasDecision((prev) => prev || alreadyDecided);

            if (!alreadyDecided && !initialPromptShownRef.current) {
                initialPromptShownRef.current = true;
                window.klaro.show(configRef.current);
            }
        };

        if (hasAnalyticsConfig) {
            loadKlaro();
        }
        return () => {
            cancelled = true;
            detachWatcher();
        };
    }, [cookieConfig, attachWatcher, detachWatcher, hasAnalyticsConfig]);

    // Ensure privacy policy links open in the same tab (avoid blank page/new tab)
    useEffect(() => {
        if (!hasAnalyticsConfig) return;

        const policyUrls = new Set<string>();
        try {
            const all = Array.from(new Set([fallbackLanguage, ...languages]));
            all.forEach((lang) => {
                // Use undefined for default language resolution to mirror Klaro's default mapping
                const isDefault = lang === fallbackLanguage;
                const rel = resolvePolicyUrl(isDefault ? undefined : lang);
                const abs = new URL(rel, window.location.origin).href;
                policyUrls.add(abs);
            });
        } catch {
            // ignore URL resolution issues
        }

        const onClick = (e: MouseEvent) => {
            const target = e.target as Element | null;
            if (!target) return;
            const anchor = (target.closest ? target.closest('a[href]') : null) as HTMLAnchorElement | null;
            if (!anchor || !anchor.href) return;

            // Limit to clicks inside Klaro element, if present
            const container = anchor.closest(`#${COOKIE_CONSENT_ELEMENT_ID}`);
            if (!container) return;

            if (policyUrls.has(anchor.href)) {
                e.preventDefault();
                // Force same-tab navigation
                window.location.assign(anchor.href);
            }
        };

        document.addEventListener('click', onClick, true);
        return () => document.removeEventListener('click', onClick, true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [hasAnalyticsConfig, languages, fallbackLanguage, resolvePolicyUrl]);

    const openManager = useCallback(() => {
        if (!hasAnalyticsConfig || !window.klaro || !configRef.current) return;
        window.klaro.show(configRef.current);
    }, [hasAnalyticsConfig]);

    return {
        manageButtonLabel: t('cookie-manager.manage-button'),
        openManager,
        shouldDisplayTrigger: hasAnalyticsConfig && isReady && hasDecision,
        shouldRender: hasAnalyticsConfig,
    };
};
